// Where the score came from: every term of the goal, measured times its
// coefficient, biggest first.
//
// **The question the page could not answer, and the reason it matters is that a
// fitness is authored blind.** `profile-shares.json` says twice that a
// coefficient means nothing without its metric beside it, and the shares are
// solved against a *yardstick* — the defaults playing themselves — so what a
// term is worth once a real champion plays it is a measurement nobody has until
// a pass has run. The worked example: one profile's first goal was 68%
// `arbitrary`, two thirds of the fitness a coin flip, and the search went
// nowhere for a day while every other profile gained.
//
// **Share of the *argument*, not of the total.** These scores go negative and
// routinely have terms pulling opposite ways, so a share of the signed sum is
// unbounded and flips sign at the crossing. The gross — the sum of the
// magnitudes — is what "how much of the argument is this term" actually means,
// and it is the same reading the share table is authored in.

import { HoverCard, styled, Text } from "../kit";
import { TERM_DOWN, TERM_UP, wash } from "./palette";
import { Legend } from "./parts";

/** How many terms of the decomposition are drawn before the tail is summed. */
const TERMS_SHOWN = 6;

const Block = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs,
  marginTop: theme.kit.space.md
}));

const Term = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.sm
}));

const Key = styled(Text)({
  width: 150,
  flexShrink: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap"
});

const Track = styled("div")(({ theme }) => ({
  flex: 1,
  minWidth: 40,
  height: 6,
  borderRadius: theme.kit.radius.pill,
  background: wash(theme.kit.color.line, 60),
  overflow: "hidden"
}));

const Bar = styled("div")({ height: "100%" });

const Points = styled(Text)({ width: 78, textAlign: "right", flexShrink: 0 });

export function Decomposition({
  metrics,
  goal,
  lean: leanShown
}: {
  metrics: Record<string, number>;
  goal: Record<string, number>;
  lean?: number;
}): React.ReactElement | null {
  const terms = Object.entries(goal)
    .filter(([, coefficient]) => typeof coefficient === "number" && coefficient !== 0)
    .map(([key, coefficient]) => ({
      key,
      coefficient,
      // A metric the champion's batch never reported is not a zero — it is a
      // term whose share cannot be read at all, which is exactly what `fixed`
      // in the share table exists for. Drawn as an absence below.
      measured: typeof metrics[key] === "number" ? metrics[key] : null
    }))
    .map((term) => ({
      ...term,
      points: term.measured === null ? null : term.measured * term.coefficient
    }))
    .sort((a, b) => Math.abs(b.points ?? 0) - Math.abs(a.points ?? 0));
  if (terms.length === 0) return null;

  // **A champion with no metrics at all is a different thing from one whose
  // terms are small, and drawing it as a stack of empty bars says the second.**
  // It happens: a champion offered before anything measured it is written with
  // its metrics absent, and two profiles published a score of ten and seventeen
  // thousand beside a breakdown of nothing.
  if (terms.every((term) => term.measured === null)) {
    return (
      <Text block tone="dim" size={11} style={{ marginTop: 12 }}>
        no measurements stored with this champion — its score came from an earlier batch, so there
        is nothing to break down
      </Text>
    );
  }

  const gross = terms.reduce((sum, term) => sum + Math.abs(term.points ?? 0), 0);
  const shown = terms.slice(0, TERMS_SHOWN);
  const rest = terms.slice(TERMS_SHOWN);
  const restPoints = rest.reduce((sum, term) => sum + (term.points ?? 0), 0);
  const share = (points: number | null) =>
    gross > 0 && points !== null ? Math.abs(points) / gross : 0;
  // The signed sum, which is what the lean actually multiplies — `gross` is the
  // sum of magnitudes and is only for sizing the bars.
  const signed = terms.reduce((sum, term) => sum + (term.points ?? 0), 0);
  const lean = leanShown ?? 1;

  return (
    <Block>
      <Legend
        text="where the score came from"
        title="The decomposition"
        rule="Each term is the champion's measured rate times the goal's coefficient. The bar is that term's share of the argument — the sum of the magnitudes, not of the signed total, which goes negative and would flip sign at the crossing. A term the batch never reported has no readable share; that is what `fixed` in the share table is for."
      />
      {shown.map((term) => (
        <Term key={term.key}>
          <HoverCard
            placement="bottom"
            title={term.key}
            card={`measured ${term.measured ?? "—"} × ${term.coefficient}`}
          >
            <Key role="display" size={11}>
              {term.key}
            </Key>
          </HoverCard>
          <Track>
            {/* Sign is the message: a term that pays and a term that charges
                are the two things a reader is separating, and a single hue
                makes a penalty look like a contribution. */}
            <Bar
              style={{
                width: `${Math.round(share(term.points) * 100)}%`,
                background: (term.points ?? 0) < 0 ? TERM_DOWN : TERM_UP
              }}
            />
          </Track>
          <Points role="display" size={11} tone={term.measured === null ? "dim" : "ink"}>
            {term.points === null ? "not measured" : Math.round(term.points).toLocaleString()}
          </Points>
        </Term>
      ))}
      {rest.length > 0 && (
        <Text block tone="dim" size={11}>
          {rest.length} smaller {rest.length === 1 ? "term" : "terms"},{" "}
          {Math.round(restPoints).toLocaleString()} between them
        </Text>
      )}
      {lean !== 1 && (
        <Text block tone="dim" size={11}>
          <Legend
            text={`×${lean.toFixed(3)} head lean`}
            title="Head lean"
            rule="`score()` multiplies the weighted sum above by 1 + HEAD_LEAN × headShare — how much of its own evaluation this army took from the network. It is a multiplier rather than a term, so it is not a bar: without it the bars do not add up to the score printed beside them, which they did not for as long as this line was missing."
          />{" "}
          on {Math.round(gross).toLocaleString()} gross = {Math.round(signed * lean).toLocaleString()}
        </Text>
      )}
    </Block>
  );
}
